import { Button, type ButtonProps } from '@mui/material';
import { forwardRef } from 'react';

const EMPHASIS_TO_VARIANT = {
  primary: 'contained',
  secondary: 'outlined',
  tertiary: 'text',
} as const satisfies Record<string, ButtonProps['variant']>;

/** Visual emphasis level. */
export type VyButtonEmphasis = keyof typeof EMPHASIS_TO_VARIANT;

/**
 * @tier atom
 *
 * Button.
 *
 * 🚧 PROPOSAL: prefer the `emphasis` prop over the raw MUI `variant`.
 * - `emphasis="primary"` — main call-to-action, filled navy. One per view.
 * - `emphasis="secondary"` — supporting action, outlined.
 * - `emphasis="tertiary"` — low-emphasis / cancel / back, text.
 *
 * `emphasis` maps onto the MUI `variant` and takes precedence when both are
 * set. Kept additive (full `ButtonProps` still accepted) so existing call
 * sites don't break.
 *
 * Intent color should be restricted to `primary` (navy) or `error`
 * (destructive) — the theme styles both. Hard-typing that restriction is a
 * follow-up once current call sites migrate (see PR).
 *
 * All styling lives in the `MuiButton` theme override; this stays a thin
 * wrapper.
 */
export interface VyButtonProps extends ButtonProps {
  /** Visual emphasis (proposed). Maps to the MUI variant; overrides `variant` when both are set. */
  emphasis?: VyButtonEmphasis;
}

export const VyButton = forwardRef<HTMLButtonElement, VyButtonProps>(
  function VyButton({ emphasis, variant, ...props }, ref) {
    return (
      <Button ref={ref} variant={emphasis ? EMPHASIS_TO_VARIANT[emphasis] : variant} {...props} />
    );
  },
);
